const note = (pitch, time, duration, velocity, layer) => ({ pitch, time, duration, velocity, layer });
const phrase = (pitches, layer, { start = 0, step = 1, duration = 0.7, velocity = 0.55 } = {}) =>
  pitches.map((pitch, i) => note(pitch, start + i * step, duration, velocity, layer));

// D4-F4-G4-A4 | G4-F4-E4-D4: an outward reach, then a path home.
export const motifs = {
  mikey: [62, 65, 67, 69, 67, 65, 64, 62],
  crew: [62, 69, 67, 65, 64, 62],
  frank: [62, 69, 67, 62],
  brian: [65, 64, 67, 69],
  jack: [50, 50, 57, 50],
  kronos: [50, 51, 50, 44],
  gauntlet: [74, 81, 76, 81],
  fall: [69, 67, 65, 64, 63, 62],
};

export const layerDefinitions = {
  ground: { instrument: 'pad', threshold: 0, gain: 0.58 },
  thread: { instrument: 'pluck', threshold: 0.2, gain: 0.85 },
  pressure: { instrument: 'percussion', threshold: 0.55, gain: 0.65 },
};
export const eras = {
  kingdom: { cutoff: 4200, layers: [1, 1, 0.55] },
  occupied: { cutoff: 2400, layers: [0.9, 0.85, 1] },
  fractured: { cutoff: 3300, layers: [0.85, 1.1, 0.8] },
  restored: { cutoff: 5200, layers: [1, 1, 0.45] },
};

function loop(id, melody, { bpm = 96, bass = [50, 53, 55, 50], instrument = 'pluck', step = 1, busy = false } = {}) {
  const layers = {
    ground: { ...layerDefinitions.ground },
    thread: { ...layerDefinitions.thread, instrument },
    pressure: { ...layerDefinitions.pressure },
  };
  const notes = bass.flatMap((pitch, i) => [
    note(pitch, i * 4, 3.7, 0.4, 'ground'),
    note(pitch + 7, i * 4, 3.7, 0.2, 'ground'),
  ]);
  notes.push(...phrase(melody, 'thread', { step, duration: step * 0.68 }));
  // Leave a breath after the first statement, then answer it in the last two bars.
  if (melody.length * step <= 8) notes.push(...phrase(melody, 'thread', { start: 8, step, duration: step * 0.56, velocity: 0.42 }));
  for (let t = 0; t < 16; t += busy ? 0.5 : 1) {
    notes.push(note(t % 4 === 0 ? 48 : 60, t, 0.12, t % 4 === 0 ? 0.5 : 0.25, 'pressure'));
  }
  return { id, bpm, beats: 16, beatsPerBar: 4, layers, notes };
}

export const cues = {
  title: loop('title', motifs.mikey, { bpm: 88, bass: [50, 55, 53, 57] }),
  fall: loop('fall', motifs.fall, { bpm: 80, bass: [50, 46, 51, 50], instrument: 'brass' }),
  pickpocket: loop('pickpocket', motifs.mikey, { bpm: 104 }),
  plan: loop('plan', motifs.brian, { step: 2 }),
  stealth: loop('stealth', [62, 69, 65, 64], { step: 2 }),
  first_heist: loop('first_heist', [62, 69, 65, 64], { bass: [50, 50, 51, 50], step: 2 }),
  month_on_run: loop('month_on_run', [62, 65, 64, 62], { bpm: 88, step: 2 }),
  recapture_fight: loop('recapture_fight', motifs.kronos, { bpm: 112, instrument: 'brass', busy: true }),
  cell: loop('cell', [62, 64, 62, 57], { bpm: 80, step: 2, bass: [50, 50, 46, 50] }),
  vent: loop('vent', motifs.brian, { step: 2 }),
  vault: loop('vault', motifs.gauntlet, { instrument: 'bell', step: 2, bass: [50, 57, 55, 50] }),
  rescue: loop('rescue', [...motifs.frank, ...motifs.brian], { bass: [50, 55, 53, 50] }),
  kronos_room: loop('kronos_room', motifs.kronos, { bpm: 96, instrument: 'brass', step: 2, bass: [50, 51, 50, 44] }),
  chase: loop('chase', motifs.mikey, { bpm: 120, busy: true }),
  escape: loop('escape', motifs.mikey, { bpm: 120, busy: true }),
  rewind: loop('rewind', [...motifs.gauntlet].reverse(), { instrument: 'bell', bass: [50, 55, 57, 50] }),
  bomb: loop('bomb', motifs.jack, { bpm: 120, instrument: 'brass', busy: true }),
  ending: loop('ending', [62, 66, 67, 69, 67, 66, 64, 62], { bpm: 88, bass: [50, 55, 54, 57] }),
  kronos: loop('kronos', motifs.kronos, { bpm: 96, instrument: 'brass', bass: [50, 51, 50, 44] }),
  gauntlet: loop('gauntlet', motifs.gauntlet, { bpm: 96, instrument: 'bell' }),
};

function accent(id, pitches, instrument, step, duration) {
  return {
    id, bpm: 120, beats: Math.max(4, pitches.length * step + duration),
    layers: { accent: { instrument, threshold: 0, gain: 0.85 } },
    notes: phrase(pitches, 'accent', { step, duration, velocity: 0.65 }),
  };
}
export const stingers = {
  freeze: accent('freeze', [81, 74, 74], 'bell', 0.125, 1.5),
  rewind: accent('rewind', [81, 76, 81, 74, 69, 62], 'bell', 0.18, 0.65),
  objective_complete: accent('objective_complete', [62, 67, 69, 74], 'pluck', 0.3, 0.7),
  alarm: accent('alarm', [62, 63, 62], 'brass', 0.25, 0.45),
  impact: accent('impact', [48, 50], 'percussion', 0.125, 0.3),
};

export const storyCueIds = ['fall', 'pickpocket', 'plan', 'first_heist', 'month_on_run',
  'recapture_fight', 'cell', 'vent', 'vault', 'rescue', 'kronos_room', 'escape', 'rewind', 'bomb', 'ending'];
